import { EditOutlined } from '@ant-design/icons';
import { type RootState } from '@reducers';
import { Button, Col, Form, Input, Row, Space } from 'antd';
import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { normalizeWarehouseProductName } from '../../fragments/normalize-warehouse-product-name';
import { useAppDispatch } from '../../hooks/use-app-dispatch';
import {
  downloaderAddWarehouseProduct,
  downloaderAddWarehouseProductAI,
  downloaderAddWarehouseProductAILocal,
} from '../../reducers/downloader/reducer';
import { SPACING } from '../../theme/tokens';

interface IForm {
  name: string;
}

function DownloaderAddWarehouseProduct({
  name,
  overrideCategoryUUIDs = [],
}: {
  name: string;
  overrideCategoryUUIDs?: string[];
}): React.ReactElement {
  const dispatch = useAppDispatch();
  const downloaderWarehouseProduct = useSelector(
    (s: RootState) => s.Downloader.warehouse.product,
  );
  const downloaderInProcess = useSelector(
    (s: RootState) => s.Downloader.inProcess,
  );

  const [form] = Form.useForm<IForm>();

  const onFinish = useCallback(
    (values: IForm): void => {
      if (!downloaderWarehouseProduct?.uuid) {
        return;
      }

      dispatch(
        downloaderAddWarehouseProduct(
          downloaderWarehouseProduct.uuid,
          normalizeWarehouseProductName(values.name),
          overrideCategoryUUIDs,
        ),
      );
    },
    [downloaderWarehouseProduct?.uuid, dispatch, overrideCategoryUUIDs],
  );

  const onFinishAI = useCallback(
    (values: IForm): void => {
      if (!downloaderWarehouseProduct?.uuid) {
        return;
      }

      dispatch(
        downloaderAddWarehouseProductAI(
          downloaderWarehouseProduct.uuid,
          normalizeWarehouseProductName(values.name),
          overrideCategoryUUIDs,
        ),
      );
    },
    [downloaderWarehouseProduct?.uuid, dispatch, overrideCategoryUUIDs],
  );

  const onFinishAILocal = useCallback(
    (values: IForm): void => {
      if (!downloaderWarehouseProduct?.uuid) {
        return;
      }

      dispatch(
        downloaderAddWarehouseProductAILocal(
          downloaderWarehouseProduct.uuid,
          normalizeWarehouseProductName(values.name),
          overrideCategoryUUIDs,
        ),
      );
    },
    [downloaderWarehouseProduct?.uuid, dispatch, overrideCategoryUUIDs],
  );

  if (!downloaderWarehouseProduct) {
    return <>---</>;
  }

  return (
    <Form form={form} initialValues={{ name }}>
      <Row gutter={[SPACING.none, SPACING.xl]}>
        <Col span={14} offset={5} className="gutter-row">
          <Form.Item
            name="name"
            label="Product name"
            rules={[
              {
                required: true,
              },
              {
                type: 'string',
                min: 3,
              },
            ]}
          >
            <Input
              size="large"
              prefix={<EditOutlined />}
              type="string"
              disabled={downloaderInProcess}
              defaultValue={name}
            />
          </Form.Item>

          <Space.Compact style={{ width: '100%' }}>
            <Button
              size="large"
              type="default"
              loading={downloaderInProcess}
              onClick={(): void => {
                form
                  .validateFields()
                  .then(onFinish)
                  .catch(() => {});
              }}
            >
              Add
            </Button>
            <Button
              size="large"
              type="primary"
              loading={downloaderInProcess}
              onClick={(): void => {
                form
                  .validateFields()
                  .then(onFinishAILocal)
                  .catch(() => {});
              }}
            >
              Add with AI (local)
            </Button>
            <Button
              size="large"
              type="dashed"
              loading={downloaderInProcess}
              onClick={(): void => {
                form
                  .validateFields()
                  .then(onFinishAI)
                  .catch(() => {});
              }}
            >
              Add with AI (all)
            </Button>
          </Space.Compact>
        </Col>
      </Row>
    </Form>
  );
}

export default DownloaderAddWarehouseProduct;
